// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Notifications tab's switches and push actions, shared by the desktop rows and
// the phone cards.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {usePushSubscriptions} from '@app/features/notification/hooks/usePushSubscriptions';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import * as PushSubscriptionService from '@app/features/platform/push/PushSubscriptionService';
import {http} from '@app/features/platform/transport/RestTransport';
import * as NotificationCommands from '@app/features/ui/commands/NotificationCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import NotificationState from '@app/features/ui/state/Notification';
import {isInstalledPwa, isPwaOnMobileOrTablet} from '@app/features/ui/utils/PwaUtils';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';

const TEST_SENT_DESCRIPTOR = msg({
	message: 'Test sent. It should arrive in a few seconds.',
	comment: 'Toast after "Send a test" in Settings > Notifications.',
});
const TEST_FAILED_DESCRIPTOR = msg({
	message: "Couldn't send a test. Try again.",
	comment: 'Toast when "Send a test" in Settings > Notifications fails.',
});

function requestPermission(): Promise<NotificationPermission> {
	return Notification.permission === 'granted' ? Promise.resolve('granted') : Notification.requestPermission();
}

export function useNotificationSettings() {
	const {i18n} = useLingui();
	const isPwa = isInstalledPwa();
	const isPwaMobile = isPwaOnMobileOrTablet();
	const {subscriptions, loading, refresh} = usePushSubscriptions(isPwaMobile);
	const toggleNotifications = (value: boolean) => {
		if (!value) {
			NotificationState.markDisabledByUser();
			NotificationCommands.permissionDenied(i18n, true);
			return;
		}
		if (typeof Notification === 'undefined') {
			void NotificationUtils.requestPermission(i18n);
			return;
		}
		void (async () => {
			const permission = await requestPermission();
			NotificationUtils.handlePermissionResult(i18n, permission);
			if (permission === 'granted' && isPwaMobile) {
				await PushSubscriptionService.registerPushSubscription();
				await refresh();
			}
		})();
	};
	const toggleUnreadBadge = (value: boolean) => {
		NotificationCommands.toggleUnreadMessageBadge(value);
	};
	/** Minutes of desktop inactivity before phones get pushes; 0 = push right away. */
	const changeAfkTimeout = async (minutes: number) => {
		try {
			await UserSettingsCommands.update({afkTimeout: minutes * 60});
		} catch {}
	};
	const registerPushSubscription = () => {
		if (typeof Notification === 'undefined') return;
		void (async () => {
			const permission = await requestPermission();
			NotificationUtils.handlePermissionResult(i18n, permission);
			if (permission !== 'granted') return;
			await PushSubscriptionService.registerPushSubscription();
			await refresh();
		})();
	};
	const forgetPushSubscriptions = async () => {
		await PushSubscriptionService.unregisterAllPushSubscriptions();
		await refresh();
	};
	/** One test push to every device with push on (POST /konvo/notifications/test). */
	const sendTestNotification = async () => {
		try {
			await http.post(Endpoints.KONVO_NOTIFICATION_TEST, {body: {}});
			ToastCommands.success(i18n._(TEST_SENT_DESCRIPTOR));
		} catch {
			ToastCommands.error(i18n._(TEST_FAILED_DESCRIPTOR));
		}
	};
	return {
		isPwa,
		isPwaMobile,
		subscriptions,
		subscriptionsLoading: loading,
		toggleNotifications,
		toggleUnreadBadge,
		changeAfkTimeout,
		registerPushSubscription,
		forgetPushSubscriptions,
		sendTestNotification,
	};
}
